// Tab Memory Page — full CRUD, search, pin, bulk actions, groups, trash,
// export/import, and URL-driven deep links (`?q=trinetra`).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import {
  Brain,
  Search,
  Plus,
  Trash2,
  Edit2,
  Clock,
  Bookmark,
  X,
  Save,
  Copy,
  Pin,
  PinOff,
  Download,
  Upload,
  ExternalLink,
  RotateCcw,
  Grid as GridIcon,
  List as ListIcon,
  Tag as TagIcon,
  Sparkles,
  Database,
  MoreVertical,
  ArrowDownAZ,
  ArrowUpDown,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../services/api-client';
import { tabService } from '../services/tabs';
import { openBrowserTabs } from '@/services/extensionBridge';
import { useTabMonitorSessionOptional } from '@/context/TabMonitorSessionContext';
import { resolveWorkspaceSessionId } from '@/utils/voiceActions';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '../components/ui/Button';
import { Input, Textarea } from '../components/ui/Input';
import { Modal, ConfirmDialog } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import type { TabMemory, TabMemoryGroup, TabMemoryPriority } from '@/types';
import {
  normalizeTabMemory,
  normalizeTabMemoryList,
  tabMemoryFromDraft,
  tabMemoryTagColor,
} from '@/types';

type View = 'all' | 'recent' | 'pinned' | 'groups' | 'trash';
type ViewMode = 'grid' | 'list';
type SortKey = 'updated' | 'visited' | 'visits' | 'title';

const GROUP_COLORS = [
  '#a855f7', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6',
];

const GROUP_DEFS_KEY = 'khiimori.tab_memory.group_defs';
const VIEW_MODE_KEY = 'khiimori.tab_memory.view_mode';
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 250;

function loadGroupDefs(): TabMemoryGroup[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(GROUP_DEFS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveGroupDefs(defs: TabMemoryGroup[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(GROUP_DEFS_KEY, JSON.stringify(defs));
  } catch {
    // ignore quota errors
  }
}

function loadViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'grid';
  try {
    const v = window.localStorage.getItem(VIEW_MODE_KEY);
    return v === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

function saveViewMode(mode: ViewMode) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(VIEW_MODE_KEY, mode);
  } catch {
    // ignore quota errors
  }
}

function safeHostname(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

function getPriorityColor(priority: TabMemoryPriority) {
  switch (priority) {
    case 'high':
      return 'text-error-400 bg-error-500/20';
    case 'medium':
      return 'text-warning-400 bg-warning-500/20';
    case 'low':
      return 'text-khiimori-text-muted bg-khiimori-bg-tertiary/30';
    default:
      return 'text-khiimori-text-muted bg-khiimori-bg-tertiary/30';
  }
}

export default function TabMemoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { workspaceSessionId } = useTabMonitorSessionOptional() ?? { workspaceSessionId: null };

  // URL-driven state
  const initialQ = searchParams.get('q') ?? '';
  const initialView = (searchParams.get('view') as View | null) ?? 'all';

  const [memories, setMemories] = useState<TabMemory[]>([]);
  const [trashed, setTrashed] = useState<TabMemory[]>([]);
  const [groupDefs, setGroupDefs] = useState<TabMemoryGroup[]>(() => loadGroupDefs());
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialQ);
  const [activeTab, setActiveTab] = useState<View>(initialView);
  const [viewMode, setViewMode] = useState<ViewMode>(() => loadViewMode());
  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [priorityFilter, setPriorityFilter] = useState<TabMemoryPriority | 'all'>('all');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // When the dashboard "Top tag" card is toggled on, the user expects
  // a second click to clear the filter. We track that explicitly.
  const [topTagPinned, setTopTagPinned] = useState(false);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openMemoryId, setOpenMemoryId] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [showWorkspacePicker, setShowWorkspacePicker] = useState(false);
  const [workspaceTabs, setWorkspaceTabs] = useState<
    { id: string; url: string; title: string; favicon?: string }[]
  >([]);
  const [loadingWorkspaceTabs, setLoadingWorkspaceTabs] = useState(false);
  const [editingMemory, setEditingMemory] = useState<TabMemory | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [hardDeletingId, setHardDeletingId] = useState<string | null>(null);
  const [showEmptyTrashConfirm, setShowEmptyTrashConfirm] = useState(false);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [showMoveToGroupModal, setShowMoveToGroupModal] = useState(false);
  const [moveTargetGroupId, setMoveTargetGroupId] = useState<string>('');

  // Group rename / delete menu.
  const [groupMenuOpen, setGroupMenuOpen] = useState<string | null>(null);
  const [renamingGroup, setRenamingGroup] = useState<{ id: string; name: string; color: string } | null>(null);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    url: '',
    title: '',
    tags: [] as string[],
    groupId: '' as string,
    priority: 'medium' as TabMemoryPriority,
    pinned: false,
    notes: '',
  });
  const [tagInput, setTagInput] = useState('');
  const [newGroup, setNewGroup] = useState({ name: '', color: GROUP_COLORS[0] });

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Persist view mode.
  useEffect(() => {
    saveViewMode(viewMode);
  }, [viewMode]);

  // Click outside the group action menu closes it.
  useEffect(() => {
    if (!groupMenuOpen) return;
    const onDocClick = () => setGroupMenuOpen(null);
    window.addEventListener('click', onDocClick);
    return () => window.removeEventListener('click', onDocClick);
  }, [groupMenuOpen]);

  // Sync URL when state changes (memory-owned params only).
  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (prev.get('section') !== 'memory') return prev;
        const next = new URLSearchParams(prev);
        if (searchQuery) next.set('q', searchQuery);
        else next.delete('q');
        if (activeTab !== 'all') next.set('view', activeTab);
        else next.delete('view');
        return next.toString() === prev.toString() ? prev : next;
      },
      { replace: true },
    );
  }, [searchQuery, activeTab, setSearchParams]);

  // Persist group defs.
  useEffect(() => {
    saveGroupDefs(groupDefs);
  }, [groupDefs]);

  // Load server-side groups (so renamed/deleted groups from another
  // tab/device are reflected) and merge them into the local defs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await apiClient.listTabMemoryGroups();
        if (cancelled) return;
        const remote: TabMemoryGroup[] = list
          .filter((g: any) => g && (g.id || g.ID))
          .map((g: any) => ({
            id: g.id ?? g.ID,
            name: g.name ?? g.Name,
            color: g.color ?? g.Color,
          }));
        if (remote.length === 0) return;
        setGroupDefs((prev) => {
          const byId = new Map(prev.map((g) => [g.id, g]));
          for (const g of remote) byId.set(g.id, g);
          return Array.from(byId.values());
        });
      } catch {
        // non-fatal: local group defs still work
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Derived: tag union across active memories.
  const availableTags = useMemo(() => {
    const set = new Set<string>();
    for (const m of memories) {
      for (const t of m.tags) set.add(t);
    }
    return Array.from(set).sort();
  }, [memories]);

  // Derived: groups from memory group_id union with local group defs.
  const groups = useMemo<TabMemoryGroup[]>(() => {
    const counts = new Map<string, number>();
    for (const m of memories) {
      if (!m.groupId) continue;
      counts.set(m.groupId, (counts.get(m.groupId) ?? 0) + 1);
    }
    const defs = new Map(groupDefs.map((g) => [g.id, g]));
    return Array.from(counts.entries())
      .map(([id, tabCount]) => {
        const def = defs.get(id);
        return {
          id,
          name: def?.name ?? `Group ${id.slice(0, 6)}`,
          color: def?.color ?? GROUP_COLORS[Math.abs(hashCode(id)) % GROUP_COLORS.length],
          tabCount,
        } satisfies TabMemoryGroup;
      })
      .sort((a, b) => (b.tabCount ?? 0) - (a.tabCount ?? 0));
  }, [memories, groupDefs]);

  // Counts.
  const counts = useMemo(() => {
    const total = memories.length;
    const pinned = memories.filter((m) => m.pinned).length;
    const thisWeek = memories.filter(
      (m) => Date.now() - new Date(m.visitedAt).getTime() <= ONE_WEEK_MS
    ).length;
    const tagFreq = new Map<string, number>();
    for (const m of memories) {
      for (const t of m.tags) tagFreq.set(t, (tagFreq.get(t) ?? 0) + 1);
    }
    let topTag = '—';
    let topTagCount = 0;
    for (const [tag, count] of tagFreq) {
      if (count > topTagCount) {
        topTag = tag;
        topTagCount = count;
      }
    }
    return { total, pinned, thisWeek, topTag };
  }, [memories]);

  const activeList = activeTab === 'trash' ? trashed : memories;

  // 7-day activity sparkline: count of memories visited per day.
  const activityBuckets = useMemo(() => {
    const days: { day: number; count: number }[] = [];
    const now = Date.now();
    for (let i = 6; i >= 0; i--) {
      const start = now - (i + 1) * 24 * 60 * 60 * 1000;
      const end = now - i * 24 * 60 * 60 * 1000;
      const count = memories.filter((m) => {
        const t = new Date(m.visitedAt).getTime();
        return t >= start && t < end;
      }).length;
      days.push({ day: i, count });
    }
    return days;
  }, [memories]);

  // Filtered list (in-memory after the server has already responded).
  const filtered = useMemo(() => {
    return activeList.filter((m) => {
      if (activeTab === 'recent') {
        return Date.now() - new Date(m.visitedAt).getTime() <= ONE_WEEK_MS;
      }
      if (activeTab === 'pinned') {
        return m.pinned;
      }
      if (activeTab === 'groups') {
        if (!selectedGroupId) return !!m.groupId;
        return m.groupId === selectedGroupId;
      }
      if (activeTab === 'trash') return true;
      if (priorityFilter !== 'all' && m.priority !== priorityFilter) return false;
      if (tagFilter && !m.tags.includes(tagFilter)) return false;
      return true;
    });
  }, [activeList, activeTab, priorityFilter, tagFilter, selectedGroupId]);

  // Sorted view of the filtered list. Pinned always float to the top.
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      switch (sortKey) {
        case 'visited':
          return new Date(b.visitedAt).getTime() - new Date(a.visitedAt).getTime();
        case 'visits':
          return b.accessCount - a.accessCount;
        case 'title':
          return (a.title || a.url).localeCompare(b.title || b.url);
        case 'updated':
        default:
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
    });
    return arr;
  }, [filtered, sortKey]);

  const hasActiveFilters =
    tagFilter !== null ||
    priorityFilter !== 'all' ||
    (activeTab === 'groups' && !!selectedGroupId);

  function clearAllFilters() {
    setTagFilter(null);
    setPriorityFilter('all');
    setSelectedGroupId(null);
    setTopTagPinned(false);
  }

  // Tracks whether the first fetch for the current (tab, search)
  // combination has completed. Prevents a brief flash of the empty
  // state when the user lands on a deep link like `?q=trinetra`.
  const hasFetchedOnceRef = useRef(false);

  // Fetch memories when search/tab changes (debounced search).
  useEffect(() => {
    let cancelled = false;
    hasFetchedOnceRef.current = false;
    const timer = window.setTimeout(async () => {
      try {
        setIsLoading(true);
        setError(null);
        if (activeTab === 'trash') {
          const data = await apiClient.getTrashedTabMemories();
          if (!cancelled) setTrashed(normalizeTabMemoryList(data));
        } else {
          let payload;
          if (searchQuery.trim()) {
            payload = await apiClient.searchTabMemories(searchQuery.trim(), {
              tag: tagFilter ?? undefined,
            });
          } else {
            payload = await apiClient.getTabMemories({
              tag: tagFilter ?? undefined,
            });
          }
          if (!cancelled) setMemories(normalizeTabMemoryList(payload));
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch memories';
        if (!cancelled) {
          setError(msg);
          if (activeTab === 'trash') setTrashed([]);
          else setMemories([]);
        }
      } finally {
        if (!cancelled) {
          hasFetchedOnceRef.current = true;
          setIsLoading(false);
        }
      }
    }, activeTab === 'trash' ? 0 : SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchQuery, activeTab, tagFilter]);

  // Keyboard shortcuts: `/` focuses search, `Esc` clears it.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === 'Escape' && document.activeElement === searchInputRef.current) {
        setSearchQuery('');
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Detail-modal "open" side effect: bump access count.
  useEffect(() => {
    if (!openMemoryId) return;
    void apiClient.recordTabMemoryAccess(openMemoryId).catch(() => undefined);
    setMemories((prev) =>
      prev.map((m) => (m.id === openMemoryId ? { ...m, accessCount: m.accessCount + 1 } : m)),
    );
  }, [openMemoryId]);

  // Bulk action helpers.
  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  // CRUD.
  const resetForm = () => {
    setFormData({
      url: '',
      title: '',
      tags: [],
      groupId: '',
      priority: 'medium',
      pinned: false,
      notes: '',
    });
    setTagInput('');
  };

  const openCreate = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openCapture = () => {
    resetForm();
    setFormData((prev) => ({
      ...prev,
      url: typeof window !== 'undefined' ? window.location.href : '',
      title: typeof window !== 'undefined' ? document.title : '',
    }));
    setShowCaptureModal(true);
  };

  const openCaptureFromWorkspace = async () => {
    resetForm();
    setShowWorkspacePicker(true);
    setLoadingWorkspaceTabs(true);
    setWorkspaceTabs([]);
    try {
      const sessionId = await resolveWorkspaceSessionId(workspaceSessionId);
      if (!sessionId) {
        toast.info('No workspace connected — open the Live tab first, or use Save this page.');
        return;
      }
      const { tabs } = await tabService.getTabsBySession(sessionId, 1, 100);
      setWorkspaceTabs(
        tabs.map((t) => ({
          id: t.id,
          url: t.url,
          title: t.title || t.url,
          favicon: t.favicon,
        })),
      );
      if (tabs.length === 0) {
        toast.info('No open tabs in your workspace');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load workspace tabs');
    } finally {
      setLoadingWorkspaceTabs(false);
    }
  };

  const selectWorkspaceTab = (tab: { url: string; title: string }) => {
    setFormData((prev) => ({
      ...prev,
      url: tab.url,
      title: tab.title,
    }));
    setShowWorkspacePicker(false);
    setShowCaptureModal(true);
  };

  const handleOpenMemoryUrl = (url: string) => {
    if (!url?.trim()) return;
    openBrowserTabs([url], true);
  };

  const openEdit = (memory: TabMemory) => {
    setEditingMemory(memory);
    setFormData({
      url: memory.url,
      title: memory.title,
      tags: memory.tags ?? [],
      groupId: memory.groupId ?? '',
      priority: memory.priority,
      pinned: memory.pinned,
      notes: memory.notes ?? '',
    });
    setShowCreateModal(true);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || formData.tags.includes(t)) return;
    setFormData((p) => ({ ...p, tags: [...p.tags, t] }));
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setFormData((p) => ({ ...p, tags: p.tags.filter((t) => t !== tag) }));
  };

  const handleSubmit = async () => {
    if (!formData.url.trim()) {
      setError('URL is required');
      return;
    }
    const draft = tabMemoryFromDraft({
      id: editingMemory?.id,
      url: formData.url,
      title: formData.title || formData.url,
      favicon: safeHostname(formData.url)
        ? `https://www.google.com/s2/favicons?domain=${safeHostname(formData.url)}&sz=64`
        : undefined,
      tags: formData.tags,
      groupId: formData.groupId || null,
      priority: formData.priority,
      pinned: formData.pinned,
      notes: formData.notes,
      metadata: { notes: formData.notes },
    });

    try {
      if (editingMemory) {
        const result = await apiClient.updateTabMemory(editingMemory.id, {
          url: draft.url,
          title: draft.title,
          tags: draft.tags,
          group_id: draft.groupId || null,
          priority: draft.priority,
          pinned: draft.pinned,
          metadata: { notes: draft.notes },
        });
        const raw =
          (result as { memory?: Record<string, unknown> })?.memory ?? (result as Record<string, unknown>);
        setMemories((prev) =>
          prev.map((m) => (m.id === editingMemory.id ? normalizeTabMemory(raw) : m)),
        );
        toast.success('Memory updated');
      } else {
        const result = await apiClient.createTabMemory({
          url: draft.url,
          title: draft.title,
          content: draft.url,
          favicon: draft.favicon,
          tags: draft.tags,
          group_id: draft.groupId || null,
          priority: draft.priority,
          pinned: draft.pinned,
          metadata: { notes: draft.notes },
        });
        const raw =
          (result as { memory?: Record<string, unknown> })?.memory ?? (result as Record<string, unknown>);
        const created = normalizeTabMemory(raw);
        setMemories((prev) => [created, ...prev]);
        toast.success('Memory saved');
      }
      setShowCreateModal(false);
      setShowCaptureModal(false);
      setEditingMemory(null);
      resetForm();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save memory';
      setError(msg);
      toast.error(msg);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiClient.trashTabMemory(id);
      setMemories((prev) => prev.filter((m) => m.id !== id));
      setDeletingId(null);
      if (openMemoryId === id) setOpenMemoryId(null);
      if (selected.has(id)) toggleSelected(id);
      toast.success('Memory moved to trash');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete memory';
      setError(msg);
      toast.error(msg);
    }
  };

  const handleHardDelete = async (id: string) => {
    try {
      await apiClient.deleteTabMemory(id);
      setTrashed((prev) => prev.filter((m) => m.id !== id));
      setHardDeletingId(null);
      if (openMemoryId === id) setOpenMemoryId(null);
      toast.success('Memory permanently deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to permanently delete memory';
      toast.error(msg);
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await apiClient.restoreTabMemory(id);
      const moved = trashed.find((m) => m.id === id);
      setTrashed((prev) => prev.filter((m) => m.id !== id));
      if (moved) setMemories((prev) => [{ ...moved, updatedAt: new Date().toISOString() }, ...prev]);
      toast.success('Memory restored');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to restore memory';
      toast.error(msg);
    }
  };

  const handlePin = async (memory: TabMemory) => {
    try {
      const next = !memory.pinned;
      setMemories((prev) => prev.map((m) => (m.id === memory.id ? { ...m, pinned: next } : m)));
      await apiClient.updateTabMemory(memory.id, { pinned: next });
    } catch (err) {
      // Revert on failure
      setMemories((prev) => prev.map((m) => (m.id === memory.id ? { ...m, pinned: memory.pinned } : m)));
      const msg = err instanceof Error ? err.message : 'Failed to update pin state';
      toast.error(msg);
    }
  };

  const handleMoveToGroup = async (ids: string[], groupId: string | null) => {
    if (ids.length === 0) return;
    try {
      const updates = ids.map((id) =>
        apiClient.updateTabMemory(id, { group_id: groupId }),
      );
      await Promise.all(updates);
      setMemories((prev) =>
        prev.map((m) => (ids.includes(m.id) ? { ...m, groupId } : m)),
      );
      toast.success(
        groupId
          ? `Moved ${ids.length} memory${ids.length === 1 ? '' : 's'} to group`
          : `Removed ${ids.length} memory${ids.length === 1 ? '' : 's'} from group`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to move memories';
      toast.error(msg);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      await apiClient.bulkDeleteTabMemories(ids, false);
      setMemories((prev) => prev.filter((m) => !selected.has(m.id)));
      toast.success(`Moved ${ids.length} memory${ids.length === 1 ? '' : 's'} to trash`);
      clearSelection();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete memories';
      toast.error(msg);
    }
  };

  const handleBulkMoveToGroup = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const groupId = moveTargetGroupId || null;
    try {
      await handleMoveToGroup(ids, groupId);
      setShowMoveToGroupModal(false);
      setMoveTargetGroupId('');
      clearSelection();
    } catch {
      // toast already shown by handleMoveToGroup
    }
  };

  const handleRenameGroup = async () => {
    if (!renamingGroup) return;
    const next = renamingGroup.name.trim();
    if (!next) {
      toast.error('Group name is required');
      return;
    }
    try {
      await apiClient.updateTabMemoryGroup(renamingGroup.id, { name: next, color: renamingGroup.color });
      setGroupDefs((prev) =>
        prev.map((g) => (g.id === renamingGroup.id ? { ...g, name: next, color: renamingGroup.color } : g)),
      );
      toast.success('Group renamed');
      setRenamingGroup(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to rename group';
      toast.error(msg);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroupId) return;
    try {
      await apiClient.deleteTabMemoryGroup(deletingGroupId);
      const id = deletingGroupId;
      setGroupDefs((prev) => prev.filter((g) => g.id !== id));
      // Detach memories locally so the sidebar count drops without a re-fetch.
      setMemories((prev) => prev.map((m) => (m.groupId === id ? { ...m, groupId: null } : m)));
      if (selectedGroupId === id) setSelectedGroupId(null);
      toast.success('Group deleted');
      setDeletingGroupId(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete group';
      toast.error(msg);
    }
  };

  const handleBulkPinToggle = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const subset = memories.filter((m) => selected.has(m.id));
    const allPinned = subset.every((m) => m.pinned);
    const nextPinned = !allPinned;
    try {
      await Promise.all(
        ids.map((id) => apiClient.updateTabMemory(id, { pinned: nextPinned })),
      );
      setMemories((prev) =>
        prev.map((m) => (selected.has(m.id) ? { ...m, pinned: nextPinned } : m)),
      );
      toast.success(`${nextPinned ? 'Pinned' : 'Unpinned'} ${ids.length} memory${ids.length === 1 ? '' : 's'}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update pin state';
      toast.error(msg);
    }
  };

  const handleEmptyTrash = async () => {
    try {
      await apiClient.emptyTabMemoryTrash();
      setTrashed([]);
      setShowEmptyTrashConfirm(false);
      toast.success('Trash emptied');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to empty trash';
      toast.error(msg);
    }
  };

  const handleExport = async (format: 'json' | 'markdown') => {
    try {
      const blob = await apiClient.exportTabMemories(format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'json' ? 'tab-memory.json' : 'tab-memory.md';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to export memories';
      toast.error(msg);
    }
  };

  const handleImport = async (file: File) => {
    try {
      setIsImporting(true);
      const text = await file.text();
      const parsed = JSON.parse(text);
      const list = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.memories) ? parsed.memories : [];
      const result = await apiClient.importTabMemories({ memories: list });
      toast.success(`Imported ${result.imported} memory${result.imported === 1 ? '' : 's'}`);
      // Refresh the list.
      const refreshed = await apiClient.getTabMemories();
      setMemories(normalizeTabMemoryList(refreshed));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to import memories';
      toast.error(msg);
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCreateGroup = () => {
    const name = newGroup.name.trim();
    if (!name) {
      setError('Group name is required');
      return;
    }
    const id = `grp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setGroupDefs((prev) => [...prev, { id, name, color: newGroup.color }]);
    setNewGroup({ name: '', color: GROUP_COLORS[0] });
    setShowNewGroupModal(false);
    toast.success('Group created');
  };

  const openMemory = sorted.find((m) => m.id === openMemoryId) ?? null;
  const isTrashView = activeTab === 'trash';

  // Renders.
  const renderFormBody = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-khiimori-text-secondary text-sm mb-2">URL *</label>
        <Input
          type="url"
          value={formData.url}
          onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))}
          placeholder="https://example.com"
          leftIcon={<ExternalLink className="h-4 w-4" />}
        />
      </div>
      <div>
        <label className="block text-khiimori-text-secondary text-sm mb-2">Title</label>
        <Input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
          placeholder="Memory title"
        />
      </div>
      <div>
        <label className="block text-khiimori-text-secondary text-sm mb-2">Tags</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {formData.tags.map((tag) => (
            <span
              key={tag}
              className={`flex items-center gap-1 px-2 py-1 bg-gradient-to-r ${tabMemoryTagColor(tag)} rounded-lg text-xs text-white`}
            >
              {tag}
              <button
                onClick={() => removeTag(tag)}
                aria-label={`Remove tag ${tag}`}
                className="hover:opacity-80"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder="Add tag"
            list="available-tags"
          />
          <datalist id="available-tags">
            {availableTags.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
          <Button onClick={addTag} variant="secondary">
            Add
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-khiimori-text-secondary text-sm mb-2">Priority</label>
          <select
            value={formData.priority}
            onChange={(e) => setFormData((p) => ({ ...p, priority: e.target.value as TabMemoryPriority }))}
            className="w-full h-10 px-3 bg-khiimori-bg-elevated border border-khiimori-border rounded-md text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <div>
          <label className="block text-khiimori-text-secondary text-sm mb-2">Group</label>
          <select
            value={formData.groupId}
            onChange={(e) => setFormData((p) => ({ ...p, groupId: e.target.value }))}
            className="w-full h-10 px-3 bg-khiimori-bg-elevated border border-khiimori-border rounded-md text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
          >
            <option value="">No group</option>
            {groupDefs.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-khiimori-text-secondary">
        <input
          type="checkbox"
          checked={formData.pinned}
          onChange={(e) => setFormData((p) => ({ ...p, pinned: e.target.checked }))}
          className="h-4 w-4 rounded border-khiimori-border bg-khiimori-bg-elevated"
        />
        <Pin className="h-4 w-4" /> Pin this memory
      </label>
      <div>
        <label className="block text-khiimori-text-secondary text-sm mb-2">Notes</label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
          placeholder="Add notes..."
          rows={3}
        />
      </div>
    </div>
  );

  const renderBulkBar = () => {
    if (isTrashView || selected.size === 0) return null;
    const allPinned = memories.filter((m) => selected.has(m.id)).every((m) => m.pinned);
    return (
      <div className="sticky top-[5.5rem] z-30 mb-4 flex flex-wrap items-center gap-2 p-3 rounded-xl bg-khiimori-bg-elevated/70 border border-khiimori-border backdrop-blur-xl">
        <span className="text-sm text-khiimori-text-primary font-medium">
          {selected.size} selected
        </span>
        <Button size="sm" variant="secondary" leftIcon={<Pin className="h-3 w-3" />} onClick={handleBulkPinToggle}>
          {allPinned ? 'Unpin' : 'Pin'} all
        </Button>
        {groupDefs.length > 0 && (
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Bookmark className="h-3 w-3" />}
            onClick={() => {
              setMoveTargetGroupId('');
              setShowMoveToGroupModal(true);
            }}
          >
            Move to group
          </Button>
        )}
        <Button size="sm" variant="danger" leftIcon={<Trash2 className="h-3 w-3" />} onClick={handleBulkDelete}>
          Delete
        </Button>
        <Button size="sm" variant="ghost" onClick={clearSelection}>
          Clear
        </Button>
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      <DashboardCard
        label="Total"
        value={counts.total}
        icon={Database}
        active={activeTab === 'all'}
        onClick={() => setActiveTab('all')}
      />
      <DashboardCard
        label="Pinned"
        value={counts.pinned}
        icon={Pin}
        active={activeTab === 'pinned'}
        onClick={() => setActiveTab('pinned')}
      />
      <DashboardCard
        label="This week"
        value={counts.thisWeek}
        icon={Clock}
        active={activeTab === 'recent'}
        onClick={() => setActiveTab('recent')}
      />
      <DashboardCard
        label="Top tag"
        value={counts.topTag}
        icon={TagIcon}
        active={topTagPinned && tagFilter === counts.topTag && counts.topTag !== '—'}
        onClick={() => {
          if (counts.topTag === '—') return;
          if (topTagPinned) {
            setTopTagPinned(false);
            setTagFilter(null);
          } else {
            setTopTagPinned(true);
            setTagFilter(counts.topTag);
          }
        }}
      />
    </div>
  );

  const renderActivitySparkline = () => {
    if (memories.length === 0) return null;
    const max = Math.max(1, ...activityBuckets.map((b) => b.count));
    const W = 140;
    const H = 32;
    const stepX = W / Math.max(1, activityBuckets.length - 1);
    const points = activityBuckets
      .map((b, i) => `${(i * stepX).toFixed(1)},${(H - (b.count / max) * H).toFixed(1)}`)
      .join(' ');
    return (
      <div className="mb-4 p-3 rounded-xl bg-khiimori-bg-elevated/5 border border-khiimori-border/10 flex items-center gap-4">
        <div className="flex items-center gap-2 text-xs text-khiimori-text-muted">
          <ArrowUpDown className="h-3 w-3" />
          7-day activity
        </div>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
          <polyline
            points={points}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-primary-400"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {activityBuckets.map((b, i) => (
            <circle
              key={i}
              cx={i * stepX}
              cy={H - (b.count / max) * H}
              r={2}
              className="fill-primary-400"
            />
          ))}
        </svg>
        <div className="text-xs text-khiimori-text-muted">
          {activityBuckets.reduce((a, b) => a + b.count, 0)} memories
        </div>
      </div>
    );
  };

  const renderGroupsSidebar = () => (
    <aside className="bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-khiimori-text-primary font-semibold">Groups</h3>
        <Button size="sm" variant="ghost" leftIcon={<Plus className="h-3 w-3" />} onClick={() => setShowNewGroupModal(true)}>
          New
        </Button>
      </div>
      {groups.length === 0 ? (
        <p className="text-sm text-khiimori-text-muted">No groups yet</p>
      ) : (
        <ul className="space-y-1">
          <li>
            <button
              className={`w-full text-left flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${
                !selectedGroupId ? 'bg-primary-500/15 text-primary-300' : 'hover:bg-khiimori-bg-tertiary/40 text-khiimori-text-secondary'
              }`}
              onClick={() => setSelectedGroupId(null)}
            >
              <span>All groups</span>
              <span className="text-xs opacity-70">{groups.reduce((a, g) => a + (g.tabCount ?? 0), 0)}</span>
            </button>
          </li>
          {groups.map((g) => (
            <li key={g.id} className="group/row relative">
              <button
                className={`w-full text-left flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${
                  selectedGroupId === g.id ? 'bg-primary-500/15 text-primary-300' : 'hover:bg-khiimori-bg-tertiary/40 text-khiimori-text-secondary'
                }`}
                onClick={() => setSelectedGroupId(g.id)}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: g.color }}
                  />
                  <span className="truncate">{g.name}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="text-xs opacity-70">{g.tabCount}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Group actions for ${g.name}`}
                    className="ml-1 p-1 rounded opacity-0 group-hover/row:opacity-100 hover:bg-khiimori-bg-tertiary/60"
                    onClick={(e) => {
                      e.stopPropagation();
                      setGroupMenuOpen(groupMenuOpen === g.id ? null : g.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        setGroupMenuOpen(groupMenuOpen === g.id ? null : g.id);
                      }
                    }}
                  >
                    <MoreVertical className="h-3 w-3" />
                  </span>
                </span>
              </button>
              {groupMenuOpen === g.id && (
                <div
                  className="absolute right-2 top-10 z-20 min-w-[120px] rounded-lg border border-khiimori-border bg-khiimori-bg-elevated shadow-lg py-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-khiimori-bg-tertiary/40"
                    onClick={() => {
                      setGroupMenuOpen(null);
                      setRenamingGroup({ id: g.id, name: g.name, color: g.color });
                    }}
                  >
                    Rename
                  </button>
                  <button
                    className="w-full text-left px-3 py-1.5 text-xs text-error-400 hover:bg-error-500/10"
                    onClick={() => {
                      setGroupMenuOpen(null);
                      setDeletingGroupId(g.id);
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );

  const renderCard = (memory: TabMemory) => (
    <motion.div
      layout
      key={memory.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18 }}
      className={`bg-khiimori-bg-elevated/5 backdrop-blur-sm border rounded-2xl p-5 transition-all group ${
        selected.has(memory.id) ? 'border-primary-500' : 'border-khiimori-border/10 hover:border-primary-500/50'
      }`}
    >
      <div className="flex items-start justify-between mb-3 gap-2">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={selected.has(memory.id)}
            onChange={(e) => {
              e.stopPropagation();
              toggleSelected(memory.id);
            }}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 rounded border-khiimori-border bg-khiimori-bg-elevated"
            aria-label={`Select ${memory.title}`}
          />
        </label>
        <div className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer" onClick={() => setOpenMemoryId(memory.id)}>
          <div className="w-10 h-10 bg-gradient-to-br from-khiimori-bg-tertiary to-khiimori-bg-tertiary rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
            {memory.favicon ? (
              <img
                src={memory.favicon}
                alt=""
                className="w-6 h-6"
                onError={(e) => (e.currentTarget.style.display = 'none')}
              />
            ) : (
              <Bookmark className="w-5 h-5 text-khiimori-text-muted" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="text-khiimori-text-primary font-semibold line-clamp-1">
              {memory.title || 'Untitled'}
            </h3>
            <p className="text-khiimori-text-muted text-xs truncate max-w-[200px]">{memory.url}</p>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            void handlePin(memory);
          }}
          aria-label={memory.pinned ? 'Unpin' : 'Pin'}
          className={`p-1.5 rounded-lg transition-colors ${
            memory.pinned
              ? 'text-warning-400 bg-warning-500/20'
              : 'text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-tertiary/40'
          }`}
        >
          <Pin className="w-4 h-4" />
        </button>
      </div>

      {memory.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {memory.tags.slice(0, 4).map((tag) => (
            <button
              key={tag}
              onClick={(e) => {
                e.stopPropagation();
                setTagFilter(tag);
              }}
              className={`px-2 py-0.5 bg-gradient-to-r ${tabMemoryTagColor(tag)} rounded text-xs text-white`}
            >
              {tag}
            </button>
          ))}
          {memory.tags.length > 4 && (
            <span className="px-2 py-0.5 bg-khiimori-bg-tertiary/30 rounded text-xs text-khiimori-text-muted">
              +{memory.tags.length - 4}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 text-khiimori-text-muted">
          <Clock className="w-3 h-3" />
          {new Date(memory.visitedAt).toLocaleDateString()}
        </div>
        <span className={`px-2 py-0.5 rounded ${getPriorityColor(memory.priority)}`}>
          {memory.priority}
        </span>
      </div>

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-khiimori-border/10 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenMemoryUrl(memory.url);
          }}
          leftIcon={<ExternalLink className="h-3 w-3" />}
        >
          Open
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            setOpenMemoryId(memory.id);
          }}
          leftIcon={<Eye className="h-3 w-3" />}
        >
          Details
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            openEdit(memory);
          }}
          leftIcon={<Edit2 className="h-3 w-3" />}
        >
          Edit
        </Button>
        {isTrashView ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                void handleRestore(memory.id);
              }}
              leftIcon={<RotateCcw className="h-3 w-3" />}
            >
              Restore
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={(e) => {
                e.stopPropagation();
                setHardDeletingId(memory.id);
              }}
              leftIcon={<Trash2 className="h-3 w-3" />}
            >
              Delete
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="danger"
            onClick={(e) => {
              e.stopPropagation();
              setDeletingId(memory.id);
            }}
            leftIcon={<Trash2 className="h-3 w-3" />}
          >
            Trash
          </Button>
        )}
      </div>
    </motion.div>
  );

  const renderListRow = (memory: TabMemory) => (
    <motion.div
      layout
      key={memory.id}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className={`bg-khiimori-bg-elevated/5 backdrop-blur-sm border rounded-xl p-4 transition-all flex items-center gap-3 ${
        selected.has(memory.id) ? 'border-primary-500' : 'border-khiimori-border/10 hover:border-primary-500/50'
      }`}
    >
      <input
        type="checkbox"
        checked={selected.has(memory.id)}
        onChange={() => toggleSelected(memory.id)}
        className="h-4 w-4 rounded border-khiimori-border bg-khiimori-bg-elevated flex-shrink-0"
        aria-label={`Select ${memory.title}`}
      />
      <div
        className="w-10 h-10 bg-gradient-to-br from-khiimori-bg-tertiary to-khiimori-bg-tertiary rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer"
        onClick={() => setOpenMemoryId(memory.id)}
      >
        {memory.favicon ? (
          <img
            src={memory.favicon}
            alt=""
            className="w-6 h-6"
            onError={(e) => (e.currentTarget.style.display = 'none')}
          />
        ) : (
          <Bookmark className="w-5 h-5 text-khiimori-text-muted" />
        )}
      </div>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setOpenMemoryId(memory.id)}>
        <h3 className="text-khiimori-text-primary font-medium truncate">{memory.title || 'Untitled'}</h3>
        <p className="text-khiimori-text-muted text-sm truncate">{memory.url}</p>
      </div>
      {memory.pinned && <Pin className="h-4 w-4 text-warning-400 flex-shrink-0" />}
      {memory.tags.length > 0 && (
        <div className="hidden md:flex gap-1 flex-shrink-0">
          {memory.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className={`px-2 py-0.5 bg-gradient-to-r ${tabMemoryTagColor(tag)} rounded text-xs text-white`}
            >
              {tag}
            </span>
          ))}
        </div>
      )}
      <span className={`px-2 py-1 rounded text-xs flex-shrink-0 ${getPriorityColor(memory.priority)}`}>
        {memory.priority}
      </span>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => handleOpenMemoryUrl(memory.url)}
          aria-label="Open in browser"
        >
          <ExternalLink className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => openEdit(memory)}
          aria-label="Edit"
        >
          <Edit2 className="h-3 w-3" />
        </Button>
        {isTrashView ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => void handleRestore(memory.id)} aria-label="Restore">
              <RotateCcw className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => setHardDeletingId(memory.id)}
              aria-label="Delete forever"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="danger"
            onClick={() => setDeletingId(memory.id)}
            aria-label="Delete"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    </motion.div>
  );

  return (
    <PageLayout title="Tab Memory" description="Intelligent tab state preservation and retrieval" icon={Brain}>
      {renderDashboard()}
      {renderActivitySparkline()}

      {/* Toolbar */}
      <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3 justify-between mb-3">
          <div className="relative flex-1 min-w-[240px] max-w-2xl">
            <Input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setActiveTab('all');
                }
              }}
              placeholder='Search memories... (press "/" to focus)'
              leftIcon={<Search className="h-4 w-4" />}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-khiimori-text-muted hover:text-khiimori-text-primary"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => void openCaptureFromWorkspace()} variant="secondary" leftIcon={<Sparkles className="h-4 w-4" />}>
              Save from workspace
            </Button>
            <Button onClick={openCapture} variant="ghost" leftIcon={<Bookmark className="h-4 w-4" />}>
              Save this page
            </Button>
            <Button onClick={openCreate} variant="primary" leftIcon={<Plus className="h-4 w-4" />}>
              Add Memory
            </Button>
            <Button onClick={() => handleExport('json')} variant="ghost" leftIcon={<Download className="h-4 w-4" />}>
              Export JSON
            </Button>
            <Button
              onClick={() => handleExport('markdown')}
              variant="ghost"
              leftIcon={<Download className="h-4 w-4" />}
            >
              Export MD
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="ghost"
              leftIcon={<Upload className="h-4 w-4" />}
              isLoading={isImporting}
            >
              Import
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleImport(f);
              }}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          {(['all', 'recent', 'pinned', 'groups', 'trash'] as const).map((tab) => {
            const count =
              tab === 'all'
                ? memories.length
                : tab === 'recent'
                ? counts.thisWeek
                : tab === 'pinned'
                ? counts.pinned
                : tab === 'groups'
                ? groups.length
                : trashed.length;
            return (
              <Button
                key={tab}
                onClick={() => setActiveTab(tab)}
                variant={activeTab === tab ? 'primary' : 'ghost'}
                size="sm"
              >
                {tab === 'all' && 'All'}
                {tab === 'recent' && 'Recent'}
                {tab === 'pinned' && 'Pinned'}
                {tab === 'groups' && 'Groups'}
                {tab === 'trash' && 'Trash'}
                <span className="ml-1.5 text-xs opacity-70">({count})</span>
              </Button>
            );
          })}
          {activeTab === 'trash' && trashed.length > 0 && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => setShowEmptyTrashConfirm(true)}
              leftIcon={<Trash2 className="h-3 w-3" />}
            >
              Empty trash
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            {!isTrashView && availableTags.length > 0 && (
              <select
                value={tagFilter ?? ''}
                onChange={(e) => setTagFilter(e.target.value || null)}
                className="h-9 px-2 text-sm rounded-md bg-khiimori-bg-elevated border border-khiimori-border text-khiimori-text-primary"
                aria-label="Filter by tag"
              >
                <option value="">All tags</option>
                {availableTags.map((t) => (
                  <option key={t} value={t}>
                    #{t}
                  </option>
                ))}
              </select>
            )}
            {!isTrashView && (
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as TabMemoryPriority | 'all')}
                className="h-9 px-2 text-sm rounded-md bg-khiimori-bg-elevated border border-khiimori-border text-khiimori-text-primary"
                aria-label="Filter by priority"
              >
                <option value="all">All priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            )}
            {!isTrashView && (
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                className="h-9 px-2 text-sm rounded-md bg-khiimori-bg-elevated border border-khiimori-border text-khiimori-text-primary"
                aria-label="Sort memories"
              >
                <option value="updated">Recently updated</option>
                <option value="visited">Recently visited</option>
                <option value="visits">Most visited</option>
                <option value="title">Title A→Z</option>
              </select>
            )}
            {hasActiveFilters && !isTrashView && (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<X className="h-3 w-3" />}
                onClick={clearAllFilters}
              >
                Clear filters
              </Button>
            )}
            <Button
              onClick={() => setViewMode('grid')}
              variant={viewMode === 'grid' ? 'primary' : 'ghost'}
              size="sm"
              aria-label="Grid view"
            >
              <GridIcon className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => setViewMode('list')}
              variant={viewMode === 'list' ? 'primary' : 'ghost'}
              size="sm"
              aria-label="List view"
            >
              <ListIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-error-500/20 border border-error-500/50 rounded-lg text-error-400 flex items-center justify-between text-sm">
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label="Dismiss error" className="p-1 hover:bg-error-500/30 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {renderBulkBar()}

      {isLoading || !hasFetchedOnceRef.current ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : sorted.length === 0 ? (
        <EmptyState
          title={
            isTrashView
              ? 'Trash is empty'
              : activeTab === 'pinned'
              ? 'No pinned memories yet'
              : activeTab === 'recent'
              ? 'No memories visited this week'
              : activeTab === 'groups'
              ? 'No groups yet'
              : searchQuery
              ? `No memories match “${searchQuery}”`
              : 'No memories yet'
          }
          description={
            isTrashView
              ? 'Deleted memories will appear here so you can restore them.'
              : 'Start saving your tab states to build your memory library.'
          }
          action={
            !isTrashView
              ? {
                  label: 'Create your first memory',
                  onClick: openCreate,
                  icon: <Plus className="h-4 w-4" />,
                }
              : undefined
          }
        />
      ) : activeTab === 'groups' ? (
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
          {renderGroupsSidebar()}
          <LayoutGroup>
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-2'}>
              <AnimatePresence>{sorted.map((m) => (viewMode === 'grid' ? renderCard(m) : renderListRow(m)))}</AnimatePresence>
            </div>
          </LayoutGroup>
        </div>
      ) : (
        <LayoutGroup>
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-2'}>
            <AnimatePresence>{sorted.map((m) => (viewMode === 'grid' ? renderCard(m) : renderListRow(m)))}</AnimatePresence>
          </div>
        </LayoutGroup>
      )}

      {/* Create / Edit modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingMemory(null);
          resetForm();
        }}
        title={editingMemory ? 'Edit Memory' : 'Add Memory'}
        size="lg"
      >
        {renderFormBody()}
        <div className="flex gap-3 mt-6">
          <Button
            variant="ghost"
            onClick={() => {
              setShowCreateModal(false);
              setEditingMemory(null);
              resetForm();
            }}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={!formData.url}
            leftIcon={<Save className="h-4 w-4" />}
            className="flex-1"
          >
            {editingMemory ? 'Save changes' : 'Create memory'}
          </Button>
        </div>
      </Modal>

      {/* Capture tab modal */}
      <Modal
        isOpen={showCaptureModal}
        onClose={() => {
          setShowCaptureModal(false);
          resetForm();
        }}
        title="Save current tab"
        description="Capture a tab into your memory library."
        size="lg"
      >
        {renderFormBody()}
        <div className="flex gap-3 mt-6">
          <Button
            variant="ghost"
            onClick={() => {
              setShowCaptureModal(false);
              resetForm();
            }}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={!formData.url}
            leftIcon={<Save className="h-4 w-4" />}
            className="flex-1"
          >
            Save memory
          </Button>
        </div>
      </Modal>

      {/* Workspace tab picker for capture */}
      <Modal
        isOpen={showWorkspacePicker}
        onClose={() => setShowWorkspacePicker(false)}
        title="Save from workspace"
        description="Pick an open tab from your connected workspace to save as a memory."
        size="lg"
      >
        {loadingWorkspaceTabs ? (
          <p className="text-sm text-khiimori-text-muted py-6 text-center">Loading workspace tabs…</p>
        ) : workspaceTabs.length === 0 ? (
          <div className="py-6 text-center space-y-3">
            <p className="text-sm text-khiimori-text-muted">
              No workspace tabs found. Open the Live tab to connect your browser, or save this page instead.
            </p>
            <Button variant="secondary" onClick={() => { setShowWorkspacePicker(false); openCapture(); }}>
              Save this page
            </Button>
          </div>
        ) : (
          <ul className="max-h-80 overflow-y-auto divide-y divide-khiimori-border/20">
            {workspaceTabs.map((tab) => (
              <li key={tab.id}>
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-2 py-3 text-left hover:bg-khiimori-bg-tertiary/30 rounded-lg transition-colors"
                  onClick={() => selectWorkspaceTab(tab)}
                >
                  <div className="w-8 h-8 rounded bg-khiimori-bg-tertiary flex items-center justify-center overflow-hidden flex-shrink-0">
                    {tab.favicon ? (
                      <img src={tab.favicon} alt="" className="w-5 h-5" onError={(e) => (e.currentTarget.style.display = 'none')} />
                    ) : (
                      <Bookmark className="w-4 h-4 text-khiimori-text-muted" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-khiimori-text-primary truncate">{tab.title}</div>
                    <div className="text-xs text-khiimori-text-muted truncate">{tab.url}</div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      {/* New group modal */}
      <Modal isOpen={showNewGroupModal} onClose={() => setShowNewGroupModal(false)} title="New group" size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-khiimori-text-secondary text-sm mb-2">Name</label>
            <Input
              value={newGroup.name}
              onChange={(e) => setNewGroup((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Research"
            />
          </div>
          <div>
            <label className="block text-khiimori-text-secondary text-sm mb-2">Color</label>
            <div className="flex flex-wrap gap-2">
              {GROUP_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setNewGroup((p) => ({ ...p, color: c }))}
                  className={`w-8 h-8 rounded-full transition-transform ${
                    newGroup.color === c ? 'ring-2 ring-white scale-110' : ''
                  }`}
                  style={{ backgroundColor: c }}
                  aria-label={`Pick color ${c}`}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="ghost" onClick={() => setShowNewGroupModal(false)} className="flex-1">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateGroup} className="flex-1">
            Create group
          </Button>
        </div>
      </Modal>

      {/* Detail modal */}
      {openMemory && (
        <Modal isOpen onClose={() => setOpenMemoryId(null)} size="lg">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-khiimori-bg-tertiary to-khiimori-bg-tertiary rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0">
              {openMemory.favicon ? (
                <img
                  src={openMemory.favicon}
                  alt=""
                  className="w-8 h-8"
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
              ) : (
                <Bookmark className="w-6 h-6 text-khiimori-text-muted" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-bold text-khiimori-text-primary">{openMemory.title}</h2>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(openMemory.url);
                  toast.success('URL copied');
                }}
                className="flex items-center gap-1 text-khiimori-text-muted hover:text-primary-400 transition-all text-sm"
              >
                <span className="truncate max-w-[280px]">{openMemory.url}</span>
                <Copy className="w-3 h-3" />
              </button>
            </div>
            <button
              onClick={() => void handlePin(openMemory)}
              className={`p-2 rounded-lg transition-colors ${
                openMemory.pinned
                  ? 'text-warning-400 bg-warning-500/20'
                  : 'text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-tertiary/40'
              }`}
              aria-label={openMemory.pinned ? 'Unpin' : 'Pin'}
            >
              {openMemory.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <DetailStat label="Visits" value={String(openMemory.accessCount)} />
            <DetailStat label="Priority" value={openMemory.priority} className={getPriorityColor(openMemory.priority)} />
            <DetailStat label="Tags" value={String(openMemory.tags.length)} />
            <DetailStat label="Pinned" value={openMemory.pinned ? 'Yes' : 'No'} />
          </div>

          {openMemory.tags.length > 0 && (
            <div className="mb-4">
              <h4 className="text-khiimori-text-secondary text-sm mb-2">Tags</h4>
              <div className="flex flex-wrap gap-1.5">
                {openMemory.tags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => {
                      setTagFilter(tag);
                      setOpenMemoryId(null);
                    }}
                    className={`px-2.5 py-1 bg-gradient-to-r ${tabMemoryTagColor(tag)} rounded-lg text-xs text-white`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {openMemory.notes && (
            <div className="mb-4">
              <h4 className="text-khiimori-text-secondary text-sm mb-2">Notes</h4>
              <p className="text-khiimori-text-muted bg-khiimori-bg-tertiary/30 rounded-xl p-3 whitespace-pre-wrap">
                {openMemory.notes}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-xs mb-4">
            <div>
              <div className="text-khiimori-text-muted">First visited</div>
              <div className="text-khiimori-text-primary">{new Date(openMemory.visitedAt).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-khiimori-text-muted">Last accessed</div>
              <div className="text-khiimori-text-primary">{new Date(openMemory.lastAccessedAt ?? openMemory.visitedAt).toLocaleString()}</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              className="flex-1"
              leftIcon={<ExternalLink className="h-4 w-4" />}
              onClick={() => handleOpenMemoryUrl(openMemory.url)}
            >
              Open in browser
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setOpenMemoryId(null);
                openEdit(openMemory);
              }}
            >
              Edit
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                if (groupDefs.length === 0) {
                  toast.error('Create a group first');
                  return;
                }
                setMoveTargetGroupId(openMemory.groupId ?? '');
                setSelected(new Set([openMemory.id]));
                setShowMoveToGroupModal(true);
              }}
            >
              Move to group
            </Button>
            {!isTrashView ? (
              <Button
                variant="danger"
                onClick={() => {
                  setOpenMemoryId(null);
                  setDeletingId(openMemory.id);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setOpenMemoryId(null);
                    void handleRestore(openMemory.id);
                  }}
                >
                  Restore
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    setOpenMemoryId(null);
                    setHardDeletingId(openMemory.id);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Soft-delete confirm */}
      <ConfirmDialog
        isOpen={!!deletingId}
        onClose={() => setDeletingId(null)}
        onConfirm={() => deletingId && void handleDelete(deletingId)}
        title="Move to trash?"
        message="The memory will be moved to the trash and can be restored later."
        confirmText="Move to trash"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Hard-delete confirm (trash) */}
      <ConfirmDialog
        isOpen={!!hardDeletingId}
        onClose={() => setHardDeletingId(null)}
        onConfirm={() => hardDeletingId && void handleHardDelete(hardDeletingId)}
        title="Delete forever?"
        message="This action cannot be undone. The memory will be permanently removed."
        confirmText="Delete forever"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Empty trash confirm */}
      <ConfirmDialog
        isOpen={showEmptyTrashConfirm}
        onClose={() => setShowEmptyTrashConfirm(false)}
        onConfirm={handleEmptyTrash}
        title="Empty trash?"
        message="All trashed memories will be permanently deleted. This cannot be undone."
        confirmText="Empty trash"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Bulk Move to group modal */}
      <Modal
        isOpen={showMoveToGroupModal}
        onClose={() => setShowMoveToGroupModal(false)}
        title="Move to group"
        description={`Assign ${selected.size} memory${selected.size === 1 ? '' : 's'} to a group. Leave empty to detach.`}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-khiimori-text-secondary text-sm mb-2">Group</label>
            <select
              value={moveTargetGroupId}
              onChange={(e) => setMoveTargetGroupId(e.target.value)}
              className="w-full h-10 px-3 bg-khiimori-bg-elevated border border-khiimori-border rounded-md text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
            >
              <option value="">No group</option>
              {groupDefs.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button
            variant="ghost"
            onClick={() => setShowMoveToGroupModal(false)}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleBulkMoveToGroup()}
            className="flex-1"
          >
            Move
          </Button>
        </div>
      </Modal>

      {/* Rename group modal */}
      <Modal
        isOpen={!!renamingGroup}
        onClose={() => setRenamingGroup(null)}
        title="Rename group"
        size="md"
      >
        {renamingGroup && (
          <div className="space-y-4">
            <div>
              <label className="block text-khiimori-text-secondary text-sm mb-2">Name</label>
              <Input
                value={renamingGroup.name}
                onChange={(e) =>
                  setRenamingGroup((p) => (p ? { ...p, name: e.target.value } : p))
                }
                placeholder="e.g. Research"
              />
            </div>
            <div>
              <label className="block text-khiimori-text-secondary text-sm mb-2">Color</label>
              <div className="flex flex-wrap gap-2">
                {GROUP_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setRenamingGroup((p) => (p ? { ...p, color: c } : p))}
                    className={`w-8 h-8 rounded-full transition-transform ${
                      renamingGroup.color === c ? 'ring-2 ring-white scale-110' : ''
                    }`}
                    style={{ backgroundColor: c }}
                    aria-label={`Pick color ${c}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="flex gap-3 mt-6">
          <Button variant="ghost" onClick={() => setRenamingGroup(null)} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleRenameGroup()}
            className="flex-1"
          >
            Save
          </Button>
        </div>
      </Modal>

      {/* Delete group confirm */}
      <ConfirmDialog
        isOpen={!!deletingGroupId}
        onClose={() => setDeletingGroupId(null)}
        onConfirm={() => void handleDeleteGroup()}
        title="Delete group?"
        message="Memories in this group will be detached (not deleted). The group itself will be removed."
        confirmText="Delete group"
        cancelText="Cancel"
        variant="warning"
      />
    </PageLayout>
  );
}

function hashCode(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h * 31 + input.charCodeAt(i)) | 0;
  }
  return h;
}

function DashboardCard({
  label,
  value,
  icon: Icon,
  active,
  onClick,
}: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
        active
          ? 'bg-primary-500/10 border-primary-500/50'
          : 'bg-khiimori-bg-elevated/5 border-khiimori-border/10 hover:border-khiimori-border/30'
      }`}
    >
      <span className="w-9 h-9 rounded-lg bg-primary-500/20 text-primary-300 flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-xs text-khiimori-text-muted">{label}</span>
        <span className="block text-lg font-semibold text-khiimori-text-primary truncate">{value}</span>
      </span>
    </button>
  );
}

function DetailStat({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className="bg-khiimori-bg-elevated/5 rounded-xl p-3 text-center">
      <div className={`text-sm font-medium ${className ?? 'text-khiimori-text-primary'}`}>{value}</div>
      <div className="text-xs text-khiimori-text-muted mt-0.5">{label}</div>
    </div>
  );
}
